import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Lock, Send, Tag, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useMessages } from "../../lib/queries";
import { useWallet } from "../../lib/wallet";
import { short, clock, fmtPrice } from "../../lib/format";

const SystemMessage = ({ m }) => (
  <div className="flex items-center gap-2 mono text-[11px] text-fog" data-testid="chat-system-msg">
    <span className="text-slate-600">{clock(m.created_at)}</span><span className="h-px flex-1 bg-line" /><span className="text-slate-400">{m.text}</span><span className="h-px flex-1 bg-line" />
  </div>
);

const ChatMessage = ({ m, mine, deal, isCreator, onAccept }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="chat-msg">
    <div className={`max-w-[78%] rounded px-3 py-2 border ${mine ? "bg-violet/15 border-violet/30" : "bg-s2 border-line2"} ${m.kind === "counter" ? "border-warn/50" : ""}`}>
      <div className="flex items-center gap-2 mono text-[10px] text-fog mb-1">
        <span className={mine ? "text-violet-300" : "text-slate-300"}>{mine ? "you" : short(m.sender)}</span>
        {m.sender === deal.seller_address && <span className="tag border-neg/40 text-neg h-4">seller</span>}
        {m.sender === deal.buyer_address && <span className="tag border-pos/40 text-pos h-4">buyer</span>}
        <span>{clock(m.created_at)}</span>
      </div>
      {m.kind === "counter" && (
        <div className="flex items-center gap-2 mb-1">
          <Tag size={12} className="text-warn" />
          <span className="mono text-sm text-warn font-semibold">{fmtPrice(m.price_usd)}/M</span>
          {m.accepted ? <span className="tag border-pos/40 text-pos"><Check size={10} /> accepted</span>
            : isCreator && deal.status === "open" && !mine && (
              <button onClick={() => onAccept(m.id)} className="btn h-6 px-2 text-[11px] bg-warn/15 border border-warn/40 text-warn hover:bg-warn/25" data-testid={`chat-accept-counter-${m.id}`}>Accept</button>
            )}
        </div>
      )}
      <div className="text-sm text-slate-100 whitespace-pre-wrap">{m.text}</div>
    </div>
  </div>
);

const MessageList = ({ msgs, deal, meAddress, isCreator, onAccept, endRef }) => (
  <div className="flex-1 overflow-auto px-4 py-3 space-y-2">
    {(msgs || []).map((m) =>
      m.kind === "system"
        ? <SystemMessage key={m.id} m={m} />
        : <ChatMessage key={m.id} m={m} mine={m.sender === meAddress} deal={deal} isCreator={isCreator} onAccept={onAccept} />
    )}
    <div ref={endRef} />
  </div>
);

const ChatComposer = ({ deal, mode, setMode, text, setText, counter, setCounter, canSend, pending, onSend }) => (
  <div className="space-y-2">
    {mode === "counter" && (
      <div className="flex items-center gap-2">
        <span className="eyebrow">Counter · USD/M</span>
        <input value={counter} onChange={(e) => setCounter(e.target.value)} type="number" step="any" placeholder={(deal.price_usd * 1e6).toFixed(2)} className="field h-8 w-36 text-xs" data-testid="deal-chat-counter-input" />
        <span className="mono text-[11px] text-fog">USD per 1M {deal.token_symbol} · listed {fmtPrice(deal.price_usd)}</span>
      </div>
    )}
    <div className="flex gap-2">
      <button onClick={() => setMode(mode === "counter" ? "text" : "counter")} disabled={deal.status !== "open"}
        className={`btn h-8 px-2.5 text-xs border ${mode === "counter" ? "border-warn text-warn bg-warn/10" : "border-line2 text-fog hover:text-white"}`} data-testid="deal-chat-counter-btn" title={deal.status !== "open" ? "Price locked once a side has funded" : "Propose a counter price"}>
        <Tag size={13} />
      </button>
      <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && canSend && onSend()}
        placeholder={mode === "counter" ? "Optional note with your counter…" : "Message the counterparty…"} className="field h-8 flex-1 text-xs font-sans" data-testid="deal-chat-input" />
      <button onClick={onSend} disabled={!canSend || pending} className="btn-primary h-8 px-3" data-testid="deal-chat-send-btn"><Send size={13} /></button>
    </div>
  </div>
);

export const DealChat = ({ deal }) => {
  const w = useWallet();
  const qc = useQueryClient();
  const { data: msgs } = useMessages(deal?.id);
  const [text, setText] = useState("");
  const [counter, setCounter] = useState("");
  const [mode, setMode] = useState("text");
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs?.length]);

  const send = useMutation({
    mutationFn: async () => {
      const sig = await w.signAction("chat", deal.id);
      if (mode === "counter") {
        const p = Number(counter) / 1e6;
        return api.sendMessage(deal.id, { ...sig, kind: "counter", price_usd: p, text: text || `Counter-offer: $${counter}/M` });
      }
      return api.sendMessage(deal.id, { ...sig, kind: "text", text });
    },
    onSuccess: () => { setText(""); setCounter(""); setMode("text"); qc.invalidateQueries({ queryKey: ["messages", deal.id] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const accept = useMutation({
    mutationFn: async (mid) => api.acceptCounter(deal.id, { ...(await w.signAction("accept_counter", deal.id)), message_id: mid }),
    onSuccess: () => { toast.success("Price updated to the counter-offer"); qc.invalidateQueries(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!deal) return null;
  const locked = deal.chat_locked;
  const isCreator = w.address && deal.creator_address === w.address;
  const canSend = w.connected && !locked && (mode === "counter" ? Number(counter) > 0 : text.trim().length > 0);

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="deal-chat-container">
      <MessageList msgs={msgs} deal={deal} meAddress={w.address} isCreator={isCreator} onAccept={accept.mutate} endRef={endRef} />

      <div className="border-t border-line p-3">
        {locked ? (
          <div className="flex items-center gap-2 mono text-[11px] text-slate-400" data-testid="deal-chat-locked"><Lock size={12} /> Thread is read-only — deal is {deal.status}. Preserved as audit trail.</div>
        ) : !w.connected ? (
          <div className="mono text-[11px] text-slate-400" data-testid="deal-chat-connect-hint">Connect a wallet to negotiate. Messages are signed by your address.</div>
        ) : (
          <ChatComposer deal={deal} mode={mode} setMode={setMode} text={text} setText={setText} counter={counter} setCounter={setCounter} canSend={canSend} pending={send.isPending} onSend={send.mutate} />
        )}
      </div>
    </div>
  );
};
